import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import useApiService from '../../component/hooks/useApiService';
import { fetchOfferRequestDetail, updateOfferRequestStatus } from '../../services/offerRequests';

const STATUS_LABELS = {
  pending: 'Beklemede',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  completed: 'Tamamlandı',
};

const STATUS_OPTIONS = [
  { value: 'pending', label: STATUS_LABELS.pending },
  { value: 'approved', label: STATUS_LABELS.approved },
  { value: 'rejected', label: STATUS_LABELS.rejected },
  { value: 'completed', label: STATUS_LABELS.completed },
];

const renderStatusLabel = (status) => STATUS_LABELS[status] || status || '-';

const normalizeDetail = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const boosts = raw?.requested_boosts || raw?.boosts || raw?.boost_codes || [];
  const boostList = Array.isArray(boosts)
    ? boosts.map((b) => ({
        code: typeof b === 'string' ? b : b?.code || b?.category || b?.name,
        request: b?.requested_amount_or_rate || b?.request || b?.note || '',
      }))
    : [];

  return {
    id: raw?.id || raw?.request_id || raw?.requestId,
    userEmail: raw?.user_email || raw?.email || raw?.client_email,
    advisorEmail: raw?.advisor_email || raw?.admin_email,
    amountUsd: raw?.investment_amount_usd || raw?.amount_usd || raw?.amount,
    expectedDate:
      raw?.expected_investment_date ||
      raw?.expected_date ||
      raw?.preferred_date ||
      raw?.investment_date,
    note: raw?.note || raw?.notes || '',
    boosts: boostList,
    status: raw?.status || 'pending',
    createdAt: raw?.created_at || raw?.createdAt,
    raw,
  };
};

const OfferRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { executeGet, executePost, extractApiError } = useApiService();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('pending');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchOfferRequestDetail(executeGet, id);
      const norm = normalizeDetail(res?.data || res);
      setDetail(norm);
      setStatus(norm.status || 'pending');
    } catch (e) {
      setError(extractApiError ? extractApiError(e) : e?.message || 'Detay yüklenemedi.');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleStatusUpdate = async (nextStatus) => {
    setSaving(true);
    setError('');
    try {
      await updateOfferRequestStatus(executePost, id, nextStatus);
      setStatus(nextStatus);
    } catch (e) {
      setError(extractApiError ? extractApiError(e) : e?.message || 'Durum güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={3}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} variant="text">
          Geri
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Offer Talebi Detayı
        </Typography>
      </Stack>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !detail ? (
          <Typography>Veri bulunamadı.</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>
                Talep ID: {detail.id || '-'}
              </Typography>
              <Chip
                label={renderStatusLabel(status)}
                color={status === 'approved' ? 'success' : status === 'rejected' ? 'error' : status === 'completed' ? 'success' : 'default'}
                size="small"
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="offer-status-label">Durum</InputLabel>
                <Select
                  labelId="offer-status-label"
                  id="offer-status-select"
                  value={status}
                  label="Durum"
                  onChange={(e) => handleStatusUpdate(e.target.value)}
                  disabled={saving}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Divider />

            <Stack spacing={1}>
              <Typography><strong>Müşteri:</strong> {detail.userEmail || '-'}</Typography>
              <Typography><strong>Danışman:</strong> {detail.advisorEmail || '-'}</Typography>
              <Typography>
                <strong>Yatırım Tutarı (USD):</strong> {detail.amountUsd ? `$${detail.amountUsd}` : '-'}
              </Typography>
              <Typography>
                <strong>Hedef Yatırım Tarihi:</strong>{' '}
                {detail.expectedDate ? new Date(detail.expectedDate).toLocaleString() : '-'}
              </Typography>
              <Typography><strong>Oluşturulma:</strong> {detail.createdAt ? new Date(detail.createdAt).toLocaleString() : '-'}</Typography>
            </Stack>

            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>Seçilen Boostlar</Typography>
              {detail.boosts?.length ? (
                <Stack spacing={1}>
                  {detail.boosts.map((b, idx) => (
                    <Paper key={`${b.code || idx}-${idx}`} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                      <Typography fontWeight={600}>{b.code || 'Boost'}</Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        {b.request || 'Ek talep belirtilmemiş.'}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">Boost bilgisi yok.</Typography>
              )}
            </Stack>

            <Divider />
            <Stack spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>Not</Typography>
              <Typography color="text.secondary">
                {detail.note ? detail.note : 'Not eklenmemiş.'}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default OfferRequestDetail;
